import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Share2, Heart, Star, CreditCard, CalendarDays, ShoppingBag, ShoppingCart, Receipt, Bike, Ticket, Wallet, ExternalLink, Globe, X } from 'lucide-react'
import { useStore, type Store, type MenuItem, type AllYouCanState } from '../../store/useStore'
import type { VariationSelection } from '../../lib/menuVariation'
import { maxPartySize, ALL_YOU_CAN_ITEM_PREFIX } from '../../lib/partySize'
import { useI18n } from '../../i18n/useI18n'
import { api, ApiError, type ExperienceProduct, type TableStayState } from '../../services/api'
import ShareSheet from '../../components/ShareSheet'
import { isWeb3Mode } from '../../lib/authMode'
import { deriveEcUrl } from '../../lib/ecUrl'
import { trackStoreView } from '../../lib/viewMetrics'
import { isSelfOrderEnabled, isDeliveryEnabled, isExperienceEnabled } from '../../lib/features'
import { storePaymentOptions } from '../../lib/storePayment'
import { useMyOrders } from '../../hooks/useMyOrders'
import { categoryGradients, type Review } from './constants'
import { storeTypeLabelKey, storeVisualKey } from '../../lib/genre'
import MenuTab from './MenuTab'
import ReviewsTab from './ReviewsTab'
import InfoTab from './InfoTab'
import ReservationModal from './ReservationModal'
import CoursesSection from './CoursesSection'
import AllYouCanSection from './AllYouCanSection'
import ExperienceSection from './ExperienceSection'
import { experienceBuyPath } from '../../lib/experienceBuyPath'
import CartSheet from '../../components/CartSheet'
import { languages } from '../../i18n/languages'
import CheckoutSheet from '../../components/CheckoutSheet'
import DeliveryCheckoutSheet from '../../components/DeliveryCheckoutSheet'
import TakeoutCheckoutSheet from '../../components/TakeoutCheckoutSheet'
import CounterRequestSheet from '../../components/CounterRequestSheet'
import SelfOrderWelcome from '../../components/SelfOrderWelcome'
import { showToast } from '../../components/Toast'
import { isBinderStore } from '../../lib/billUnit'

/** #2036 時刻だけを出す (「残り 25 分」は画面を開いた時点の値で、置くと古くなる) */
const hhmm = (iso?: string) => {
  if (!iso) return ''
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export default function StoreDetail() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { stores, currentStore, setCurrentStore, wallet, isLoggedIn, user, cart, addToCart, updateCartQuantity, setCartTableNumber, setCartDeliveryMode, favoriteStores, toggleFavorite, guestDietaryAllergens, guestDietaryStyles } = useStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t, locale, setLocale } = useI18n()

  // #1460 この店が見られたことを数える。ゲストも対象 (訪日客は未ログインで見て回る)。
  // 店舗の読み込み成否に関係なく「開かれた」事実を数えるので、ここに置く。
  useEffect(() => {
    if (id) trackStoreView(id)
  }, [id])

  const [reviews, setReviews] = useState<Review[]>([])
  const [apiStore, setApiStore] = useState<Store | null>(null)
  const [storeLoadFailed, setStoreLoadFailed] = useState(false)
  // #296 quota-blocked distinct from generic load failure — when the
  // owner hit their Firestore quota, the API returns 429 and we must
  // NOT fall back to the Zustand cache (otherwise the customer sees
  // stale store data despite the owner being enforced-offline).
  const [storeBlockedByQuota, setStoreBlockedByQuota] = useState(false)
  const [activeTab, setActiveTab] = useState<'menu' | 'products' | 'experience' | 'courses' | 'reviews' | 'info'>('menu')
  // #1248/#1292: 体験商品 (チケット)。あれば「体験」タブ + チケット購入導線を出す (内容ドリブン)。
  const [experienceProducts, setExperienceProducts] = useState<ExperienceProduct[]>([])
  const [showReservationModal, setShowReservationModal] = useState(false)
  const [showCartSheet, setShowCartSheet] = useState(false)
  const [showCheckoutSheet, setShowCheckoutSheet] = useState(false)
  const [showDeliveryCheckout, setShowDeliveryCheckout] = useState(false)
  const [showTakeoutCheckout, setShowTakeoutCheckout] = useState(false)
  // #1402 レジで払う (受付番号を発行して店員に読んでもらう)
  const [showCounterRequest, setShowCounterRequest] = useState(false)
  const [showShareSheet, setShowShareSheet] = useState(false)
  // #2115 言語の選択シート (メニュー閲覧のみの店)
  const [showLanguageSheet, setShowLanguageSheet] = useState(false)
  // #968 卓セッション: undefined=未解決, null=active無し(ウェルカム表示), {…}=active有り(スキップ)。
  const [tableSession, setTableSession] = useState<{ partySize?: number } | null | undefined>(undefined)
  // #2115 メニュー閲覧のみの店で、卓 QR から来たときの卓番号。カートが無いので
  // ここに置く (「ご注文はこちら」の URL の {table} に差し込むためだけに使う)。
  const [menuOnlyTable, setMenuOnlyTable] = useState<string | null>(null)
  // #1535 会計 CTA に出す卓全体の伝票 (合計 + 同卓のほかの注文のステータス)。
  // ラベル無しで自分の分の額を出すと「卓の合計」と誤読され、会計時に金額が
  // 跳ねて揉める。店内伝票=卓単位 (Airレジ等) に合わせ、CTA は卓の額と
  // 進行状況を正とする (支払い範囲はシート内で選べる)。null = 取得前/卓なし。
  const [ctaTableBill, setCtaTableBill] = useState<{
    totalAmount: number
    otherStatuses: string[]
  } | null>(null)
  const [welcomeDone, setWelcomeDone] = useState(false)
  const { orders: myOrders } = useMyOrders()

  // 売り切れ等でメニューが古くなった時に呼び直せるよう関数に切り出す。
  // 客が「厨房へ送信」で売り切れに弾かれた時、ここを叩いて売り切れ表示を最新化する。
  // #2344 `quiet` は**読み直し**のとき。失敗しても画面は前の内容のまま残す
  // — ここで storeBlockedByQuota / storeLoadFailed を立てると、下の `!store` の
  // early return でページごと差し替わり、開いているカートや決済シートが消える
  // (3D セキュアから戻った瞬間に 429 を踏むと、それだけで決済が中断する)。
  const loadStore = useCallback(async (quiet = false) => {
    if (!id) return
    try {
      const data = await api.getStore(id)
      setApiStore(data as Store)
      if (data.recentReviews) setReviews(data.recentReviews)
    } catch (err) {
      if (quiet) {
        console.error('[StoreDetail] 読み直しに失敗 (表示は前のまま):', err)
        return
      }
      if (err instanceof ApiError && err.status === 429) {
        setStoreBlockedByQuota(true)
      } else {
        setStoreLoadFailed(true)
      }
    }
  }, [id])

  useEffect(() => { void loadStore() }, [loadStore])

  // #2344 在庫は店側の会計で減る。開いたときの値を持ち続けると、売れた後も
  // 「残り 24 点」と出たままになる。**常時見張らない** (店を開いている客の数だけ
  // 読みが増える) 代わりに、画面に戻ってきたときに読み直す — 客が見るのは
  // 戻ってきた瞬間なので、そこが合っていれば足りる。
  // 注文自体はサーバが売り切れを弾くので、ここは表示を合わせるためのもの。
  // 合図は visibilitychange だけにする (focus と両方だと、タブに戻るたび 2 回読む)。
  useEffect(() => {
    const refresh = () => { if (!document.hidden) void loadStore(true) }
    document.addEventListener('visibilitychange', refresh)
    return () => document.removeEventListener('visibilitychange', refresh)
  }, [loadStore])

  // #1248/#1292: この店が体験商品 (チケット) を持つか。持てば「体験」タブと購入導線を出す。
  useEffect(() => {
    if (!id || !isExperienceEnabled()) return
    let cancelled = false
    api.listExperienceProducts({ storeId: id })
      .then((r) => { if (!cancelled) setExperienceProducts(r.products) })
      .catch(() => { /* 体験なしとして扱う */ })
    return () => { cancelled = true }
  }, [id])

  const zustandStore = id ? stores.find((s) => s.id === id) : undefined
  // On 429 we deliberately skip the Zustand fallback — the owner is
  // enforced-offline, showing cached data would make the enforcement
  // invisible to the customer.
  const store: Store | undefined = storeBlockedByQuota
    ? undefined
    : (apiStore || zustandStore)

  const isFavorited = !!store && favoriteStores.some((f) => f.storeId === store.id)

  useEffect(() => {
    if (store && (!currentStore || currentStore.id !== store.id)) {
      setCurrentStore(store)
    }
  }, [store, currentStore, setCurrentStore])

  // #949 (管理番号_011): the table QR carries an opaque token (`?t={token}`),
  // not the raw number. Resolve it server-side to the table number, stamp the
  // cart, and strip the param so the number never lingers in the URL. The raw
  // `?table=N` form is no longer accepted (it would let anyone guess other
  // tables). `seatTokenRef` keeps the token so a login round-trip before the
  // first order can return to the same seat.
  const seatTokenRef = useRef<string | null>(null)
  const resolvingTokenRef = useRef<string | null>(null)
  useEffect(() => {
    if (!store || !isSelfOrderEnabled() || store.orderingMode !== 'self-order') return
    // #2039 バインダー形式の店では、客は卓に「着席」しない (伝票は店員が開ける)。店が客の
    // スマホ注文を受けている場合だけ、**開いている伝票**に注文を付けられるようにする。
    const binderStore = isBinderStore(store)
    const token = searchParams.get('t')
    if (!token) return
    if (binderStore && !store.binderCustomerOrder) {
      // 注文は受けていない。**トークンだけは URL から消す** — 伝票を読める唯一の鍵で、
      // 誰でも見られるこのページの URL に残すと、送っただけで伝票が見えてしまう。
      searchParams.delete('t')
      setSearchParams(searchParams, { replace: true })
      return
    }
    seatTokenRef.current = token
    if (resolvingTokenRef.current === token) return  // already resolving this token
    resolvingTokenRef.current = token
    const storeId = store.id
    ;(async () => {
      try {
        if (binderStore) {
          // 「開いている」の判定はサーバの 1 か所 (伝票のページと同じ口) に寄せる。卓セッションの
          // 有無だけで見ると、セッションが切れて未払いだけ残った伝票で、伝票のページは「注文
          // できる」と言うのにここは「スタッフへ」と言う食い違いになる。
          const bill = await api.getBinderBill(storeId, token)
          // 伝票のページへ戻るのに要るので、トークンはこの端末に覚えておく (URL からは消す)
          try { sessionStorage.setItem(`binderToken:${storeId}`, token) } catch { /* 無くても動く */ }
          if (bill.open) {
            setCartTableNumber(store, bill.binderNumber)
            setTableSession({ partySize: bill.partySize ?? undefined })
          } else {
            // 開いていない伝票。客はバインダーを開けられない (時間料金の起点は店員の操作)
            showToast({ type: 'info', message: t('binderBill.askStaff') })
          }
          return
        }
        const res = await api.resolveTable(storeId, token)
        if (res?.tableNumber) {
          // Re-read store from the store ref via setCartTableNumber's closure
          // is fine; store is stable for this storeId.
          setCartTableNumber(store, String(res.tableNumber))
          // #968 active セッションがあればウェルカム/人数をスキップ、無ければ(null)表示。
          setTableSession(res.session ?? null)
        }
      } catch (e) {
        console.error('[StoreDetail] table token resolve failed:', e)
      } finally {
        // Strip the token from the URL regardless of resolve outcome.
        searchParams.delete('t')
        setSearchParams(searchParams, { replace: true })
      }
    })()
  }, [store, searchParams, setSearchParams, setCartTableNumber, t])

  // #2115 メニュー閲覧のみの店。カートも決済も無いが、卓 QR から来たことだけは
  // 分かるようにする — 「ご注文はこちら」が卓ごとの URL のとき、**その卓の QR を
  // 読んだ人にだけ**リンクを出すため (店の外から注文されるのを防ぐ)。
  const menuOnlyResolvedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!store || store.orderingMode !== 'menu-only') return
    const token = searchParams.get('t')
    if (!token || menuOnlyResolvedRef.current === token) return
    menuOnlyResolvedRef.current = token
    const storeId = store.id
    ;(async () => {
      try {
        const res = await api.resolveTable(storeId, token)
        if (res?.tableNumber) setMenuOnlyTable(String(res.tableNumber))
      } catch (e) {
        // 卓が分からなくてもメニューは見られる (ボタンが出ないだけ)
        console.error('[StoreDetail] menu-only table resolve failed:', e)
      } finally {
        searchParams.delete('t')
        setSearchParams(searchParams, { replace: true })
      }
    })()
  }, [store, searchParams, setSearchParams])

  // #2039 バインダー形式の店は、注文を店員が受けるのが基本 (サーバも客の注文を断る)。客のページは
  // メニューを見るだけになる。店が「スマホ注文を受ける」を ON にしているときだけカートを出す —
  // それでも注文できるのは、店員が開けた伝票の QR を読んだ客だけ (サーバが同じ規則で断る)。
  const binderStore = isBinderStore(store)
  const selfOrderActive = isSelfOrderEnabled() && store?.orderingMode === 'self-order'
    && (!binderStore || !!store?.binderCustomerOrder)
  // #284: when the customer arrives via the /delivery flow (?mode=delivery)
  // and the store supports it, we pivot the UI into delivery mode. Items
  // are added to the cart with orderType='delivery' and the dine-in
  // "お会計" button is replaced with "注文を確定して決済" which opens the
  // delivery checkout sheet (address + upfront payment).
  const deliveryMode =
    isDeliveryEnabled() &&
    searchParams.get('mode') === 'delivery' &&
    !!store?.deliveryEnabled
  // #1213 カウンター受け取り: ?mode=takeout。卓も住所も不要で、先払い後に
  // 呼び出し番号で受け取る。持ち帰り限定ではなく店内飲食 (フードコート) も対象。
  const takeoutMode = searchParams.get('mode') === 'takeout' && !!store?.takeoutEnabled
  useEffect(() => {
    if (!deliveryMode || !store) return
    // Carry the coords DeliveryStores forwarded on the URL. Drop them
    // from the URL after stamping so a refresh doesn't keep re-stamping,
    // but keep ?mode=delivery so the delivery UI stays visible.
    const latStr = searchParams.get('lat')
    const lngStr = searchParams.get('lng')
    const lat = latStr ? parseFloat(latStr) : NaN
    const lng = lngStr ? parseFloat(lngStr) : NaN
    const coords = Number.isFinite(lat) && Number.isFinite(lng)
      ? { lat, lng }
      : undefined
    setCartDeliveryMode(store, coords)
    if (latStr || lngStr) {
      searchParams.delete('lat')
      searchParams.delete('lng')
      setSearchParams(searchParams, { replace: true })
    }
  }, [deliveryMode, store, setCartDeliveryMode, searchParams, setSearchParams])

  // #686: owner の Stripe Connect KYC 未完了 (charges_enabled=false) の場合、
  // 決済 CTA を全て disable してバナーで「準備中」を伝える。一覧では filter
  // 済なので通常導線では到達せず、ここは bookmark / 共有リンク等の直 URL
  // アクセス時のフォールバック。`undefined` (= API がフィールドを返してない
  // 旧クライアント、本来発生しない) は enabled として扱う。
  // #972 現金のみ運用 (cashOnlyMode) — KYC 未完了でもアプリ内「現金会計」は使える。
  // KYC(Stripe) が塞いでいないか。`undefined` は従来どおり enabled 扱い（旧クライアント
  // フォールバック）。アプリ内カード決済 (Stripe) は KYC 完了かつ現金のみ OFF の時だけ、
  // 現金会計は KYC 完了 or 現金のみ ON のどちらかで可（= 一覧表示可能な店）。
  // 判定表は lib/storePayment.ts (QRScan と共通。#1769 ポイント払いは「現金のみ運用」でも可)
  const {
    cashOnly,
    cardInApp: cardInAppAllowed,
    cash: cashAllowed,
    points: pointsAllowed,
  } = storePaymentOptions(store)
  // #1800 先払い (テイクアウト / 出前) はカードかポイントのどちらかがあれば出せる。
  // 現金のみ運用でポイントを受ける店 (#1769) はポイントだけで先払いできる
  const prepayAllowed = cardInAppAllowed || pointsAllowed
  // #711: 臨時休業 (operability='paused') の場合も決済不可。検索 list には backend
  // の filter で出ないが、QR スキャン / bookmark 経由で詳細ページに到達した時に
  // 「現在休業中」のバナーを出して CTA を全て disable する。
  const isPaused = store?.operability === 'paused'
  // カードも現金も使えない店だけ「準備中」。現金のみ店は cashAllowed=true で塞がない。
  const noPaymentAvailable = !cardInAppAllowed && !cashAllowed
  const paymentBlocked = noPaymentAvailable || isPaused

  // #284: cart is active for either self-order (dine-in) or delivery mode.
  // #1213 カウンター受け取りもカート導線を使う
  const cartActive = selfOrderActive || deliveryMode || takeoutMode
  const cartForThisStore = cartActive && cart && cart.storeId === store?.id ? cart : null
  const cartItemCount = cartForThisStore ? cartForThisStore.items.reduce((n, i) => n + i.quantity, 0) : 0
  // Active bill for this store = unpaid orders (deferred-payment model).
  // Cancelled orders are excluded from the bill.
  // #1213 先払いチャネル (出前 / カウンター受け取り) は卓の伝票に含めない。
  // 決済前に落ちた注文が「お会計（未払い）」に合算されると、卓の会計で二重に
  // 払わせる導線になる (MyOrders の openBills と同じ判断)。
  const isDeferredPayment = (o: { orderType?: string }) =>
    o.orderType !== 'delivery' && o.orderType !== 'takeout'
  const unpaidForThisStore = selfOrderActive && store
    ? myOrders.filter((o) => o.storeId === store.id && !o.paid && o.status !== 'cancelled' && isDeferredPayment(o))
    : []
  const unpaidTotal = unpaidForThisStore.reduce((n, o) => n + o.totalAmount, 0)
  // Cross-store unpaid: customer has an open bill at another store. Real
  // restaurants can't have you eating at two places at once, so we block
  // ordering here until the other bill is settled.
  const otherStoreUnpaid = store
    ? myOrders.filter((o) => o.storeId !== store.id && !o.paid && o.status !== 'cancelled' && isDeferredPayment(o))
    : []
  const otherStoreId = otherStoreUnpaid[0]?.storeId
  // 一覧に出ない店 (unlisted) は stores に無いので、注文に残した店名 (storeName) を先に見る
  const otherStoreName = otherStoreId
    ? otherStoreUnpaid[0]?.storeName || stores.find((s) => s.id === otherStoreId)?.name || otherStoreId
    : null
  // #2140 体験の店 (紹介ページの体験) では止めない。体験は始めるたびに別の店になるので、
  // 前の体験の注文 (会計していない) が「別の店の未会計」に数えられて注文できなくなる
  const blockedByOtherStore = selfOrderActive && !store?.isTrial && otherStoreUnpaid.length > 0
  // #314 task #2: when a store has set up table QR (tableCount > 0) we
  // refuse to take orders from visitors who didn't scan a table — the
  // kitchen has no way to deliver food to a "Guest #XXXX" with no known
  // location. Delivery/take-out stores would need a different rule, but
  // that's a separate feature; the rule is table-scan-or-bust for now.
  // Customers also can't self-cancel from /my-orders yet, so an accidental
  // table-less order has no escape hatch — another reason to gate hard.
  // #947 (管理番号_004) Derive the seated table from the customer's own open
  // (unpaid, non-cancelled) dine-in orders at this store. This lets them keep
  // adding orders after the first one without re-scanning the QR — the seat
  // lives as long as the bill is open and disappears once it's paid (a new
  // visit needs a fresh scan + staff approval). The server still gates
  // ordering (first order per table needs approval), so this is a UX
  // convenience, not an auth boundary.
  const derivedSeatedTable = unpaidForThisStore.find((o) => o.tableNumber)?.tableNumber
  const seatedTableNumber = cartForThisStore?.tableNumber || derivedSeatedTable
  // #2115 メニュー閲覧のみ。注文も会計も他社の仕組みで回っている店の入口。
  const menuOnly = store?.orderingMode === 'menu-only'
  // 「ご注文はこちら」の飛び先。`{table}` 入り = 卓ごとの深いリンクなので、
  // **その卓の QR を読んだ人にだけ**出す (誰でも見えるページに貼ると、店の外から
  // 注文できてしまう)。`{table}` 無し = 店で 1 本の共通 URL は常に出す。
  const externalOrderUrl = (() => {
    if (!menuOnly) return ''
    const table = menuOnlyTable || seatedTableNumber
    // ① 卓ごとに貼られた URL (実運用はこれ。卓の QR から来た人にだけ出す)
    const perTable = table ? (store?.externalOrderUrlsByTable || {})[String(table)] : ''
    if (perTable && perTable.trim()) return perTable.trim()
    const raw = (store?.externalOrderUrl || '').trim()
    if (!raw) return ''
    // ② 共通 URL に {table} が入っていれば卓番号を差し込む (卓が分かる時だけ)
    if (raw.includes('{table}')) {
      return table ? raw.replace(/\{table\}/g, encodeURIComponent(String(table))) : ''
    }
    // ③ 店で 1 本の共通 URL (卓は相手の画面で選んでもらう) は常に出す
    return raw
  })()
  const requireTable = selfOrderActive && (store.tableCount ?? 0) > 0 && !seatedTableNumber

  // #1535 卓全体の伝票を取得して CTA に反映。自分の注文が変わった時に加え、
  // 同席者・店員入力の追加も拾えるよう 30 秒ごとに更新する。
  useEffect(() => {
    if (!selfOrderActive || !id || !derivedSeatedTable || unpaidTotal <= 0) {
      setCtaTableBill(null)
      return
    }
    let cancelled = false
    const load = () => {
      api.getTableBill(id, derivedSeatedTable, user?.id)
        .then((res) => {
          if (cancelled || !res || typeof res.totalAmount !== 'number') return
          setCtaTableBill({
            // #2155 テーブル全員分は卓を締めるので、お通し・席料込みの額 (会計シートと同じ)
            totalAmount: typeof res.tablePayable === 'number' ? res.tablePayable : res.totalAmount,
            otherStatuses: ((res.otherOrders || []) as { status?: string }[])
              .map((o) => o.status || ''),
          })
        })
        .catch(() => { /* 取れない間は従来の自分の分表示に留める */ })
    }
    load()
    const timer = window.setInterval(load, 30_000)
    return () => { cancelled = true; window.clearInterval(timer) }
  }, [selfOrderActive, id, derivedSeatedTable, unpaidTotal, user?.id])

  // Status breakdown for the order-progress hint shown next to "お会計".
  // #1535 卓の伝票が取れている間は**卓全体** (自分のこの卓の分 + 同卓のほかの注文)
  // で数える — 金額をテーブル全員分にするなら進行状況も卓全体が正 (店内伝票=卓単位)。
  const ctaStatuses = ctaTableBill
    ? [
        ...unpaidForThisStore
          .filter((o) => o.tableNumber === derivedSeatedTable)
          .map((o) => o.status),
        ...ctaTableBill.otherStatuses,
      ]
    : unpaidForThisStore.map((o) => o.status)
  const receivedCount = ctaStatuses.filter((s) => s === 'received').length
  const preparingCount = ctaStatuses.filter((s) => s === 'preparing').length
  const servedCount = ctaStatuses.filter((s) => s === 'served').length

  // #1181 案B: 物販商品 (isRetail) はメニューと別タブ。お土産店 (料理ゼロ) では
  // メニュータブ自体を出さない。両方ゼロの店はメニュータブを空表示でフォールバック。
  // store は初回ロード中 undefined になり得るため null 安全に導出する。
  const foodMenuItems = (store?.menu || []).filter((m) => !m.isRetail)
  const retailMenuItems = (store?.menu || []).filter((m) => m.isRetail === true)
  // #1248/#1292 あるべき論: 飲食かそれ以外か。メニュー(食べ物)/コース/予約は飲食のみ。
  // 体験(チケット)はどの店でも持てる属性、物販(商品)は retail 商品がある店。
  const hasExperience = experienceProducts.length > 0
  // #1353 お土産タブは店主が非表示にできる。**在庫管理にだけ使いたい店がある** —
  // 商品を登録して数は管理したいが、客のメニューには出したくないケース。
  // 未設定は表示 (既存店の見え方を変えない)。
  const retailVisible = store?.retailVisibleToCustomers !== false
  const hasRetail = retailMenuItems.length > 0 && retailVisible
  const isFoodStore = store?.genre !== 'experience' && store?.genre !== 'souvenir'
  // #2344 体験 (チケット) しか扱わない店。**支払いの案内はこの画面ではなくチケットの
  // 画面にある** (「今すぐ支払う / 当日その場で支払う」をそこで選ぶ)。ここで
  // 「現金のみです。ご注文後、レジで現金でお支払いください」と出すと、
  // 注文もレジ会計も無い店で、チケット画面の案内と食い違って見える。
  const experienceOnlyStore = hasExperience && !hasRetail && foodMenuItems.length === 0
  const courseEnabled = !!store?.reservationEnabled && isFoodStore
  const visibleTabs = [
    // メニュー(食べ物)は飲食店のみ。食べ物がある、または他に出す物が無い飲食店の既定表示。
    ...(isFoodStore && (foodMenuItems.length > 0 || (!hasRetail && !hasExperience)) ? (['menu'] as const) : []),
    ...(hasRetail ? (['products'] as const) : []),
    ...(hasExperience ? (['experience'] as const) : []),
    ...(courseEnabled ? (['courses'] as const) : []),
    // #2115 メニュー閲覧のみの店では**レビューは一生増えない** (レビューは
    // アプリでのお支払いの後に書いてもらうもので、この方式では決済が無い)。
    // 空のまま「まだレビューがありません」とだけ出ると、いつか付くように読める。
    // 既にレビューがある店 (後から方式を変えた) は、隠すと読めなくなるので出す。
    ...(menuOnly && reviews.length === 0 ? [] : (['reviews'] as const)),
    'info' as const,
  ]
  // 既定 activeTab='menu' がお土産店では非表示のため、可視タブへフォールバック
  const currentTab = (visibleTabs as string[]).includes(activeTab) ? activeTab : visibleTabs[0]

  // #968 初回スキャン時のみ「ウェルカム→人数確認」を表示。token があり(=スキャン直後)・
  // 卓に active セッションが無く(null)・まだ未完了の時のみ。別端末の同卓2人目は resolve-table
  // が返す session でスキップされる。
  // #2039 伝票のページへの戻り口。トークンは QR を読んだこの端末にだけ残っている
  const binderBillLink = (() => {
    if (!binderStore || !id) return null
    try {
      const token = sessionStorage.getItem(`binderToken:${id}`)
      return token ? `/stores/${id}/binder?t=${encodeURIComponent(token)}` : null
    } catch {
      return null
    }
  })()

  const showWelcome =
    selfOrderActive &&
    !binderStore &&   // #2039 バインダーは店員が開ける。客に人数を聞いて「入店」させない
    (store.tableCount ?? 0) > 0 &&
    !!seatTokenRef.current &&
    !!seatedTableNumber &&
    tableSession === null &&
    !welcomeDone

  // #947 Stamp the derived seat onto a fresh dine-in cart that lacks a table
  // (e.g. after the previous order cleared the cart, or after a reload) so the
  // next order carries the right tableNumber without a re-scan. Guarded so it
  // only fires once per cart — once stamped, the condition is false.
  useEffect(() => {
    if (!store || !selfOrderActive || !derivedSeatedTable) return
    if (cart && cart.storeId === store.id && cart.orderType !== 'delivery' && !cart.tableNumber) {
      setCartTableNumber(store, derivedSeatedTable)
    }
  }, [store, selfOrderActive, derivedSeatedTable, cart, setCartTableNumber])

  // Build the URL we want to land back on after login. #949: preserve the
  // opaque token (`?t={token}`) — not the raw number — so a customer who
  // scanned a table QR before logging in returns to the same seat without the
  // number ever appearing in the URL. (If they're already seated via an open
  // order, #947's derived seat covers it, so the token isn't required.)
  const stashReturnPath = () => {
    const path = `/stores/${store?.id ?? ''}`
    const tokenParam = seatTokenRef.current ? `?t=${seatTokenRef.current}` : ''
    sessionStorage.setItem('postLoginRedirect', path + tokenParam)
  }

  // #1353 タブバー（sticky top-0）の実測高さ。メニューのカテゴリチップを
  // **この下に** sticky させるために渡す。タブ名は幅次第で 2 行に折り返すので
  // 固定値を決め打ちすると、狭い端末でチップがタブの裏に潜って消える。
  //
  // useEffect + useRef ではなく **callback ref** で測る。この画面は store の
  // ロード中に `if (!store) return` で早期 return するため、mount 時の effect が
  // 走る時点ではタブバーがまだ DOM に無い。deps [] の effect は再実行されないので
  // 高さが 0 のまま残り、チップが z-40 のタブバーの裏に潜って**消えて見えた**。
  const [tabBarHeight, setTabBarHeight] = useState(0)
  const tabBarObserver = useRef<ResizeObserver | null>(null)
  const tabBarRef = useCallback((el: HTMLDivElement | null) => {
    tabBarObserver.current?.disconnect()
    tabBarObserver.current = null
    if (!el) return
    setTabBarHeight(el.getBoundingClientRect().height)
    const ro = new ResizeObserver(() => setTabBarHeight(el.getBoundingClientRect().height))
    ro.observe(el)
    tabBarObserver.current = ro
  }, [])

  // #1353 この卓で放題プランが既に動いているか。動いていれば重ねて注文させない
  // （サーバも 409 で弾くが、押せてしまう UI にはしない）。
  // 状態そのものを持つ。店員が人数を訂正したとき、**客が自分の画面で反映を
  // 確認できる**ようにするため（口頭の言い値にしない）。定期的に引き直す。
  const [tablePlan, setTablePlan] = useState<AllYouCanState | null>(null)
  // #2036 卓の「ご利用時間 / ラストオーダー」。卓 QR の token は URL から消えるので、
  // 卓番号で読み直す (残り時間は減っていく)。時刻を持たない古いセッションでは null。
  const [tableStay, setTableStay] = useState<TableStayState | null>(null)
  const tablePlanActive = !!tablePlan?.active
  // 放題プラン適用中に 0 円になる商品。**表示を実際の請求額に合わせる**ためだけに使う
  // （0 円の確定はサーバ側。ここを信用して課金判断はしない）。
  // #1587 カートに入れただけ (まだ店員未承認) のプランも 0 円表示の対象にする。
  //
  // 卓に適用済みのプラン (`tablePlan`) だけを見ていたため、「飲み放題 + ドリンク」を
  // 1 回で注文する客には**満額が表示されていた**。サーバは承認時に 0 円へ引き直すので、
  // 表示だけが実際の請求額とズレる状態だった。
  const [pendingPlanItemIds, setPendingPlanItemIds] = useState<string[] | null>(null)
  const cartHasPlan = !!cartForThisStore?.items.some(
    (i) => i.menuItemId.startsWith(ALL_YOU_CAN_ITEM_PREFIX))
  const coveredItemIdList = tablePlanActive
    ? tablePlan?.itemIds
    : (cartHasPlan ? (pendingPlanItemIds ?? undefined) : undefined)
  const coveredItemIds = new Set(coveredItemIdList || [])
  const cartTotal = cartForThisStore
    ? cartForThisStore.items.reduce(
      // #2031 放題の対象でも、有料の選択の分は請求される (CartSheet の priceOf と同じ)
      (n, i) => n + (coveredItemIds.has(i.menuItemId) ? (i.optionsPrice ?? 0) : i.price) * i.quantity, 0)
    : 0
  useEffect(() => {
    if (!store?.id || !seatedTableNumber) {
      setTablePlan(null)
      return
    }
    let cancelled = false
    const load = () => {
      api.getTableAllYouCan(store.id, String(seatedTableNumber))
        .then((r) => { if (!cancelled) setTablePlan(r.allYouCan) })
        .catch((e) => console.error('[StoreDetail] table plan fetch failed', e))
    }
    load()
    const id = window.setInterval(load, 60_000)
    return () => { cancelled = true; window.clearInterval(id) }
  }, [store?.id, seatedTableNumber])

  // #2036 滞在時間の状態も同じ間隔で読み直す (ラストオーダーの案内・時間切れの表示)。
  // **卓セッションが出来た時にも読み直す** — 卓番号はウェルカム(人数確認)の前に決まるので、
  // 卓番号だけを見ていると「まだ席が無い」状態で 1 回読んで終わり、案内が出るのが
  // 次の 60 秒後になってしまう。
  useEffect(() => {
    if (!store?.id || !seatedTableNumber) {
      setTableStay(null)
      return
    }
    let cancelled = false
    const load = () => {
      api.getTableStay(store.id, String(seatedTableNumber))
        .then((r) => { if (!cancelled) setTableStay(r.stay) })
        .catch((e) => console.error('[StoreDetail] table stay fetch failed', e))
    }
    load()
    const id = window.setInterval(load, 60_000)
    return () => { cancelled = true; window.clearInterval(id) }
  }, [store?.id, seatedTableNumber, tableSession])

  // #1246: カート追加はゲスト開放 (cart は Zustand ローカルで完結)。ログイン
  // 要求は注文送信/決済の瞬間まで後ろ倒し (CartSheet / 各 Checkout CTA 側)。
  // 「登録なしで注文直前まで」がファーストタッチの離脱を減らす。
  const handleAddToCart = (item: MenuItem, selection?: VariationSelection) => {
    if (!store) return
    addToCart(store, item, selection)
    showToast({ type: 'success', message: t('cart.added', { name: item.name }) })
  }

  // #1353 放題プランをカートへ。数量 = 人数なので、追加後に数量を打ち直す
  // （addToCart は 1 個ずつ足す作りのため）。
  //
  // #1583 上限を**明示的に渡す**。放題は「数量 = 人数」なので店舗設定の
  // 1 組の最大人数まで許す必要があり、既定の 99 で丸められると 130 名の卓で
  // 飲み放題を入れられない。store は手元にあるのでここで解決する。
  const handleAddPlanToCart = (
    item: MenuItem, quantity: number, coveredIds?: string[],
  ) => {
    if (!store) return
    addToCart(store, item)
    updateCartQuantity(item.id, quantity, maxPartySize(store))
    // #1587 このプランが 0 円にする商品を覚えておき、カート表示を請求額に合わせる
    setPendingPlanItemIds(coveredIds ?? null)
  }

  const handlePayClick = () => {
    if (!isLoggedIn) {
      stashReturnPath()
      navigate('/login')
      return
    }
    if (isWeb3Mode()) {
      if (wallet && wallet.balanceJPYC <= 0) {
        navigate('/charge')
        return
      }
      navigate('/scan', { state: { storeId: store?.id } })
    } else {
      navigate('/scan', { state: { storeId: store?.id } })
    }
  }

  if (!store) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-white">
        {storeBlockedByQuota ? (
          <div className="text-center px-6 max-w-sm">
            <p className="font-display font-bold text-base text-text-primary">
              {t('storeDetail.quotaBlockedTitle')}
            </p>
            <p className="mt-2 text-text-secondary font-body text-sm leading-relaxed">
              {t('storeDetail.quotaBlockedDesc')}
            </p>
            <button onClick={() => navigate(-1)} className="mt-4 text-navy font-medium tap-effect">
              {t('storeDetail.goBack')}
            </button>
          </div>
        ) : storeLoadFailed ? (
          <div className="text-center px-6">
            <p className="text-text-secondary font-body">{t('storeDetail.notFound')}</p>
            <button onClick={() => navigate(-1)} className="mt-4 text-navy font-medium tap-effect">{t('storeDetail.goHome')}</button>
          </div>
        ) : (
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" />
        )}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-warm-white safe-top pb-40">
      {/* #968 セルフオーダー入店の導入: ウェルカム→人数確認（初回スキャン時のみ・全画面） */}
      {showWelcome && seatedTableNumber && (
        <SelfOrderWelcome
          storeId={store.id}
          storeName={store.name}
          storeImageUrl={store.selfOrderImageUrl || store.imageUrl}
          tableNumber={String(seatedTableNumber)}
          token={seatTokenRef.current || ''}
          maxPartySize={maxPartySize(store)}
          onComplete={(partySize) => {
            setTableSession({ partySize })
            setWelcomeDone(true)
          }}
        />
      )}
      {/* Hero Image */}
      <div className={`relative h-[220px] bg-gradient-to-br ${categoryGradients[storeVisualKey(store)] || categoryGradients.restaurant}`}>
        <img src={store.imageUrl} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/10" />

        <button
          onClick={() => navigate(-1)}
          className="absolute top-[calc(env(safe-area-inset-top,0px)+12px)] left-4 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md tap-effect z-10"
        >
          <ArrowLeft size={20} className="text-text-primary" />
        </button>

        <div className="absolute top-[calc(env(safe-area-inset-top,0px)+12px)] right-4 flex gap-2 z-10">
          <button
            aria-label={t('share.button.store')}
            onClick={() => setShowShareSheet(true)}
            className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center tap-effect"
          >
            <Share2 size={18} className="text-white" />
          </button>
          {/* #2115 メニュー閲覧のみの店の言語切替。**この方式では他に切り替える場所が無い**
            * (切替はセルフオーダーの歓迎画面にしかなく、QR でメニューだけ見るお客様は
            * 端末の言語で決まったものから変えられなかった)。**ページの作りは他の店と
            * 同じまま**にしたいので、帯ではなくここのアイコンに入れる。
            * セルフオーダーも同じ (#2140)。切替は最初の歓迎画面にしかなく、メニューに進んだ後は
            * 言語を変えられなかった */}
          {(menuOnly || selfOrderActive) && (
            <button
              aria-label={t('storeDetail.languageSwitch')}
              onClick={() => setShowLanguageSheet(true)}
              className="h-10 px-3 bg-white/20 backdrop-blur-sm rounded-full flex items-center gap-1 tap-effect"
            >
              <Globe size={18} className="text-white" />
              {/* アイコンだけだと何の設定か分からない。**今の言語**を短く添える */}
              <span className="font-body text-xs font-semibold text-white">
                {languages.find((l) => l.code === locale)?.short ?? ''}
              </span>
            </button>
          )}
          <button
            aria-label={isFavorited ? t('favorites.remove') : t('favorites.add')}
            aria-pressed={isFavorited}
            onClick={() => store && toggleFavorite(store)}
            className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center tap-effect"
          >
            <Heart size={18} className={isFavorited ? 'text-yokoso-red fill-yokoso-red' : 'text-white'} />
          </button>
        </div>

        <div className="absolute bottom-4 left-4 right-4">
          <h1 className="font-jp font-bold text-2xl text-white drop-shadow-lg">{store.name}</h1>
          <p className="text-white/80 font-body text-sm mt-0.5 drop-shadow">{store.nameEn}</p>
        </div>
      </div>

      {/* Table-QR seat banner — always visible (not just inside the bottom
       * CTA) so the customer can confirm at a glance which table the
       * order will be tied to. Mirrors the badge the store-side sees in
       * StoreOrders, so both ends share the same visible label. */}
      {selfOrderActive && seatedTableNumber && !binderStore && (
        <div className="mx-4 mt-3 rounded-xl bg-blue-600 text-white px-4 py-2.5 flex items-center justify-center gap-2 shadow-sm">
          <span className="text-xs font-body opacity-80">{t('storeDetail.tableBannerLabel')}</span>
          <span className="font-display text-base font-bold">
            {t('storeDetail.tableBadge', { table: seatedTableNumber })}
          </span>
        </div>
      )}
      {/* #2039 バインダー形式: 注文が付く伝票の番号と、今の金額を確かめるページへの戻り口 */}
      {selfOrderActive && seatedTableNumber && binderStore && (
        <div className="mx-4 mt-3 rounded-xl bg-navy text-white px-4 py-2.5 flex items-center justify-between gap-3 shadow-sm">
          <span className="font-display text-base font-bold">
            {t('binderBill.binderNo', { n: seatedTableNumber })}
          </span>
          {binderBillLink && (
            <Link to={binderBillLink} className="font-body text-xs underline underline-offset-2 tap-effect">
              {t('binderBill.viewBill')}
            </Link>
          )}
        </div>
      )}

      {/* #2036 ご利用時間とラストオーダー。**残り時間ではなく時刻を出す** — 「残り 25 分」は
        * 画面を開いた時点の値で、置いておくと古くなる。時刻なら時計と突き合わせられる。
        * ラストオーダーが近づいたら色を変え、時間を過ぎたら理由を出す (止める設定の店では
        * 注文できなくなるため、何が起きたか分かるようにする)。 */}
      {selfOrderActive && seatedTableNumber && tableStay && (
        <div
          className={`mx-4 mt-2 rounded-xl px-4 py-2.5 font-body text-xs leading-relaxed ${
            tableStay.stayOver
              ? 'bg-yokoso-red/10 text-yokoso-red border border-yokoso-red/20'
              : tableStay.lastOrderPassed
                ? 'bg-amber-50 text-amber-800 border border-amber-200'
                : 'bg-gray-50 text-text-secondary border border-gray-100'
          }`}
        >
          {tableStay.stayOver ? (
            <p className="font-semibold">
              {tableStay.stopOrdersAtStayLimit
                ? t('storeDetail.stayOverStopped')
                : t('storeDetail.stayOver')}
            </p>
          ) : (
            <>
              <p className={tableStay.lastOrderPassed ? 'font-semibold' : ''}>
                {tableStay.lastOrderPassed
                  ? t('storeDetail.stayLastOrderNow', { time: hhmm(tableStay.staysUntilAt) })
                  : t('storeDetail.stayUntil', { time: hhmm(tableStay.staysUntilAt) })}
              </p>
              {/* 店が案内を「出さない」にしていると時刻が来ない → 行ごと出さない */}
              {tableStay.lastOrderAt && (
                <p className="mt-0.5 opacity-90">
                  {t('storeDetail.stayLastOrder', { time: hhmm(tableStay.lastOrderAt) })}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* #284 delivery mode banner — matcha-colored so it's visually
       * different from the blue table-QR banner. Signals clearly that the
       * customer is in the delivery flow rather than browsing dine-in. */}
      {deliveryMode && (
        <div className="mx-4 mt-3 rounded-xl bg-matcha text-white px-4 py-2.5 flex items-center justify-center gap-2 shadow-sm">
          <Bike size={16} />
          <span className="font-display text-sm font-bold">
            {t('delivery.modeBanner')}
          </span>
        </div>
      )}

      {/* Store Info Summary */}
      <div className="px-4 pt-4 flex items-center gap-1.5 flex-wrap">
        {/* レビューが無い店は「★0」でなく「新着」表示 */}
        {store.rating > 0 ? (
          <>
            <Star size={16} className="text-gold fill-gold" />
            <span className="font-body font-semibold text-text-primary">{store.rating}</span>
          </>
        ) : (
          <span className="font-body font-semibold text-text-secondary">{t('stores.newStore')}</span>
        )}
        <span className="text-text-tertiary mx-1">&bull;</span>
        <span className="font-body text-text-secondary text-sm">{t(storeTypeLabelKey(store))}</span>
        <span className="text-text-tertiary mx-1">&bull;</span>
        {store.operability !== 'paused' ? (
          <span className="bg-matcha/15 text-matcha px-2 py-0.5 rounded-full text-xs font-body font-semibold">{t('storeDetail.openNow')}</span>
        ) : (
          <span className="bg-gray-200 text-text-secondary px-2 py-0.5 rounded-full text-xs font-body font-semibold">{t('storeDetail.closed')}</span>
        )}
      </div>

      {/* Tab Bar — #1181 案B: 物販商品 (isRetail) はコースと同じく独立タブ。
        * メニュータブは料理のみ (お土産店では非表示)。セルフオーダーのカートに
        * お土産が混ざらない。両方無い店はメニュータブを空表示でフォールバック。 */}
      <div ref={tabBarRef} className="sticky top-0 z-40 bg-white border-b border-gray-100 mt-4">
        <div className="flex">
          {visibleTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                currentTab === tab ? 'text-navy border-b-2 border-navy' : 'text-text-tertiary'
              }`}
            >
              {tab === 'menu' && `${t('storeDetail.tabMenu')}（${foodMenuItems.length}）`}
              {tab === 'products' && `${t('storeDetail.tabProducts')}（${retailMenuItems.length}）`}
              {tab === 'experience' && t('storeDetail.tabExperience')}
              {tab === 'courses' && t('storeDetail.tabCourses')}
              {tab === 'reviews' && `${t('storeDetail.tabReviews')}（${reviews.length}）`}
              {tab === 'info' && t('storeDetail.tabInfo')}
            </button>
          ))}
        </div>
      </div>

      {/* Cross-store unpaid blocker banner */}
      {blockedByOtherStore && otherStoreName && (
        <div className="mx-4 mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3">
          <p className="font-body text-xs text-amber-900 leading-relaxed">
            {t('storeDetail.blockedByOtherStore', { store: otherStoreName })}
          </p>
          <button
            onClick={() => navigate('/my-orders')}
            className="mt-2 text-xs font-body font-semibold text-amber-900 underline"
          >
            {t('storeDetail.goToMyOrders')}
          </button>
        </div>
      )}

      {/* Table-QR required notice is intentionally NOT shown as a top
       * banner here — that would harass anyone who's just browsing /stores
       * and tapped through to look at the menu. Instead we surface it
       * inside the dish modal in MenuTab (where the customer is actually
       * trying to order), via the tableRequiredNotice prop below. */}

      {/* Tab Content */}
      {/* #1353 放題プラン（飲み放題・食べ放題）。メニューの先頭に 1 商品として並べる。
        * 卓が確定していないと注文できない（サーバも弾く）ので、着席中だけ描画する。 */}
      {currentTab === 'menu' && selfOrderActive && !blockedByOtherStore && seatedTableNumber && (
        <AllYouCanSection
          store={store}
          tableNumber={String(seatedTableNumber)}
          seatedPartySize={tableSession?.partySize}
          planActive={tablePlanActive}
          activeState={tablePlan}
          onAdd={handleAddPlanToCart}
        />
      )}
      {currentTab === 'menu' && (
        <MenuTab
          // #2029 客の表示の選択は店ごと。別の店へ移ったら作り直して、その店の選択で始める
          key={store.id}
          layoutStore={store}
          variationGroups={store.variationGroups}
          menu={foodMenuItems}
          userAllergens={user?.dietaryAllergens ?? guestDietaryAllergens}
          userDietary={user?.dietaryStyles ?? guestDietaryStyles}
          customCategories={store.customCategories}
          // #1353 放題プラン適用中は対象商品を ¥0 で見せる（実際の請求額に合わせる）
          allYouCanItemIds={coveredItemIdList}
          stickyTop={tabBarHeight}
          onAddToCart={(deliveryMode || takeoutMode || (selfOrderActive && !blockedByOtherStore && !requireTable)) ? handleAddToCart : undefined}
          // #1531 従来は店舗情報タブにしか出ておらず、メニューではボタンが無いだけだった
          tableRequiredNotice={requireTable && !blockedByOtherStore ? t(binderStore ? 'binderBill.scanToOrder' : 'storeDetail.tableQrRequired') : undefined}
        />
      )}
      {/* 商品 (物販) は閲覧のみ — 購入は店頭レジ (スキャン販売) か EC カード経由。
        * セルフオーダーのカートには載せない (在庫・厨房伝票と交わらないため)。 */}
      {currentTab === 'products' && (
        <>
          {/* #1184 自社EC 導線: EC vendor にリンクされた店だけ、お土産タブの先頭に表示
            * (他タブには出さない — メニューを見ている客の邪魔をしない)。ecVendorSlug は
            * admin が ec_vendors.payStoreId を設定したとき store doc に非正規化される。
            * EC URL が導出できない環境 (demo 初期等) では非描画。 */}
          {store.ecVendorSlug && deriveEcUrl() && (
            <a
              href={`${deriveEcUrl()}/shops/${store.ecVendorSlug}`}
              className="tap-card mx-4 mt-3 flex items-center gap-3 rounded-2xl bg-gradient-to-br from-yokoso-red to-yokoso-red-dark p-3.5 text-left text-white shadow-card"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15">
                <ShoppingBag size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display text-sm font-semibold">{t('storeDetail.ecShop.title')}</div>
                {/* #1748 **文言は固定して、対応範囲はラベルで出す。**
                  * 以前は「海外配送でお届け」の 1 文だけだったので、越境をやらない店に
                  * 誤った案内が出ていた。かといって文を出し分けると、今度は越境 ON の店が
                  * 「海外だけ」に読める (**国内配送は全店で常に可** — #1759)。
                  * 「オンラインで取り寄せられる」ことだけを本文で言い、どこへ送れるかは
                  * ラベルで足す。国内は無条件なので常に出す。 */}
                <div className="text-[11px] opacity-90">{t('storeDetail.ecShop.desc')}</div>
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] font-medium leading-tight">
                    {t('storeDetail.ecShop.shipDomestic')}
                  </span>
                  {store.ecCrossBorderAllowed && (
                    <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] font-medium leading-tight">
                      {t('storeDetail.ecShop.shipOverseas')}
                    </span>
                  )}
                </div>
              </div>
              <ArrowRight size={18} className="shrink-0 opacity-90" />
            </a>
          )}
          {/* **物販にも店のカテゴリを渡す。**
            *
            * 既定カテゴリ (MENU_CATEGORIES) は main / side / drink ... と飲食しか無いため、
            * おみやげ店の商品はどれにも当てはまらない。`[]` を渡していた間は店が
            * customCategories を作っても効かず、**何品あっても全部「その他」の 1 塊**に
            * なっていた。渡す側で握り潰さず `categoryEntries` に判断させる — あちらは
            * **実際に商品があるカテゴリだけ**を出すので、飲食用のカテゴリが物販タブへ
            * 漏れることはない。 */}
          <MenuTab
            menu={retailMenuItems}
            userAllergens={user?.dietaryAllergens ?? guestDietaryAllergens}
            userDietary={user?.dietaryStyles ?? guestDietaryStyles}
            customCategories={store.customCategories}
          />
        </>
      )}
      {currentTab === 'experience' && <ExperienceSection products={experienceProducts} trialStore={store?.isTrial === true} />}
      {currentTab === 'courses' && <CoursesSection storeId={store.id} />}
      {currentTab === 'reviews' && (
        <ReviewsTab reviews={reviews} userNationality={user?.nationality} menuOnly={menuOnly} />
      )}
      {currentTab === 'info' && (
        <InfoTab
          store={store}
          tableRequiredNotice={requireTable && !blockedByOtherStore ? t(binderStore ? 'binderBill.scanToOrder' : 'storeDetail.tableQrRequired') : undefined}
        />
      )}

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] bg-white/80 backdrop-blur-md border-t border-gray-100 p-4 safe-bottom z-50 space-y-2">
        {selfOrderActive && seatedTableNumber && (
          <div className="flex items-center justify-center">
            <span className="rounded-full bg-blue-50 border border-blue-100 px-3 py-1 text-[11px] font-body font-semibold text-blue-700">
              {binderStore ? t('binderBill.binderNo', { n: seatedTableNumber }) : t('storeDetail.tableBadge', { table: seatedTableNumber })}
            </span>
          </div>
        )}
        {/* #686: owner KYC 未完了で決済 CTA を全て隠した時に「準備中」を明示。
            通常導線では一覧 filter で到達しないが、bookmark 等の直 URL アクセス
            時のフォールバック表示。
            #711: paused の場合は別文言 ("現在休業中") を出す。priority は paused
            の方が上 (admin-side の理由でなく owner 自身の意思表示なので、より
            明確に伝える)。 */}
        {isPaused && (
          <div className="rounded-xl bg-yokoso-red/10 border border-yokoso-red/30 px-3 py-2.5">
            <p className="font-display font-bold text-sm text-text-primary">
              {t('storeDetail.pausedTitle')}
            </p>
            <p className="font-body text-xs text-text-secondary mt-1 leading-relaxed">
              {t('storeDetail.pausedDesc')}
            </p>
          </div>
        )}
        {!isPaused && !menuOnly && !experienceOnlyStore && noPaymentAvailable && (
          <div className="rounded-xl bg-gold/10 border border-gold/30 px-3 py-2.5">
            <p className="font-display font-bold text-sm text-text-primary">
              {t('storeDetail.paymentDisabledTitle')}
            </p>
            <p className="font-body text-xs text-text-secondary mt-1 leading-relaxed">
              {t('storeDetail.paymentDisabledDesc')}
            </p>
          </div>
        )}
        {/* #972 現金のみ運用店: アプリ内カード決済不可・現金会計のみである旨を案内。 */}
        {!isPaused && !menuOnly && !experienceOnlyStore && cashOnly && (
          <div className="rounded-xl bg-matcha/10 border border-matcha/30 px-3 py-2.5">
            <p className="font-display font-bold text-sm text-text-primary">
              {t('storeDetail.cashOnlyTitle')}
            </p>
            <p className="font-body text-xs text-text-secondary mt-1 leading-relaxed">
              {t('storeDetail.cashOnlyDesc')}
            </p>
            {pointsAllowed && (
              <p className="font-body text-xs text-matcha mt-1 leading-relaxed">
                {t('storeDetail.cashOnlyPointsOk')}
              </p>
            )}
          </div>
        )}
        {cartForThisStore && cartItemCount > 0 && (
          <button
            onClick={() => setShowCartSheet(true)}
            className="w-full h-12 bg-gold text-white rounded-xl font-display font-semibold text-sm flex items-center justify-center gap-2 tap-effect active:scale-[0.98] transition-transform"
          >
            <ShoppingCart size={18} />
            {t('cart.viewCart', { count: cartItemCount, total: cartTotal.toLocaleString() })}
          </button>
        )}
        {/* #968 「メニューを追加」で店舗に戻った客が「ご注文状況」へ戻れる導線。
          * これが無いとブラウザの戻る以外に注文一覧へ戻れず UX が悪い。 */}
        {selfOrderActive && !deliveryMode && !takeoutMode && unpaidForThisStore.length > 0 && (
          <button
            onClick={() => navigate('/my-orders')}
            className="w-full text-center font-body text-xs text-text-secondary underline underline-offset-2 tap-effect py-1"
          >
            {t('storeDetail.viewOrderStatus')}
          </button>
        )}
        {selfOrderActive && !deliveryMode && !takeoutMode && unpaidForThisStore.length > 0 && !paymentBlocked && (
          unpaidForThisStore.some((o) => o.status === 'pending_approval') ? (
            // #947 未承認注文がある間は会計不可（店員の承認後に payable）。
            <div className="rounded-xl bg-purple-50 border border-purple-200 px-4 py-2.5 text-center">
              <span className="font-body text-xs text-purple-800 leading-relaxed">
                {t('storeDetail.awaitingApprovalNote')}
              </span>
            </div>
          ) : (
            <div className="rounded-xl bg-yokoso-red text-white tap-effect active:scale-[0.98] transition-transform">
              <button
                onClick={() => setShowCheckoutSheet(true)}
                className="w-full px-4 py-2.5 flex flex-col items-center"
              >
                <div className="flex items-center gap-2">
                  <Receipt size={16} />
                  <span className="font-display font-semibold text-sm">
                    {/* #1535 卓に着席中は「テーブル全員分」の額を出す (代表払いが通常
                       * ケース。ラベル無しの自分の分の額は卓の合計と誤読される)。
                       * 卓が取れない間 (卓なし / fetch 前) は従来の自分の分 */}
                    {ctaTableBill
                      ? t('checkout.ctaTable', { total: ctaTableBill.totalAmount.toLocaleString() })
                      : t('checkout.cta', { total: unpaidTotal.toLocaleString() })}
                  </span>
                </div>
                <span className="font-body text-[11px] text-white/85 mt-0.5">
                  {t('storeDetail.orderProgress', {
                    received: receivedCount,
                    preparing: preparingCount,
                    served: servedCount,
                  })}
                </span>
              </button>
            </div>
          )
        )}

        {/* #284 delivery CTA — pre-payment per order, replaces the dine-in
         * "お会計" flow entirely. Visible whenever delivery mode is on and
         * the cart has items; min-order check happens inside the sheet. */}
        {deliveryMode && cartForThisStore && cartItemCount > 0 && !paymentBlocked && prepayAllowed && (
          <button
            onClick={() => {
              if (!isLoggedIn) {
                stashReturnPath()
                navigate('/login')
                return
              }
              setShowDeliveryCheckout(true)
            }}
            className="w-full h-14 bg-navy text-white rounded-xl font-display font-semibold text-base flex items-center justify-center gap-2 tap-effect active:scale-[0.98] transition-transform"
          >
            <Bike size={18} />
            {t('delivery.placeOrder')}
          </button>
        )}
        {/* #1213 店頭受け取り CTA — 先払いして呼び出し番号で受け取る。卓・住所とも不要。
          * #1767 **カード前払いとレジ払いを別々に出す。** 以前は両方を cardInAppAllowed で
          * 囲っていたため、現金のみの店 (cardInAppAllowed=false) では**レジ払いまで消え**、
          * カートに入れた後に注文を確定する導線が 1 つも無かった。レジ払いは金が動かず
          * (#1402)、まさに現金のみの店のための経路なので cashAllowed で出す。 */}
        {takeoutMode && cartForThisStore && cartItemCount > 0 && !paymentBlocked && (prepayAllowed || cashAllowed) && (
          <>
            {prepayAllowed && (
              <>
                {/* #1398 カウンター受け取りはカード先払いなのでログインが要る。
                  * 押した瞬間に何の説明も無くログイン画面へ飛ぶと「弾かれた」と
                  * 受け取られるので、**押す前に**理由と、カートが消えないことを伝える。 */}
                {!isLoggedIn && (
                  <p className="mb-2 rounded-lg bg-navy/5 px-3 py-2 font-body text-[11px] leading-relaxed text-text-secondary">
                    {t('takeout.loginRequiredNote')}
                  </p>
                )}
                <button
                  onClick={() => {
                    if (!isLoggedIn) {
                      stashReturnPath()
                      navigate('/login')
                      return
                    }
                    setShowTakeoutCheckout(true)
                  }}
                  className="w-full h-14 bg-navy text-white rounded-xl font-display font-semibold text-base flex items-center justify-center gap-2 tap-effect active:scale-[0.98] transition-transform"
                >
                  <ShoppingBag size={18} />
                  {isLoggedIn ? t('takeout.payButton') : t('takeout.loginAndPayButton')}
                </button>
              </>
            )}
            {/* #1402 レジで払う経路。**ログイン不要**で、客が自分で選んだ内容を
              * 受付番号でレジに渡す。店員が口頭で聞いて入力する手間と聞き間違いが消える。
              * #1767 カード前払いが無い店ではこれが唯一の注文導線なので、主ボタンの見た目にする。 */}
            {cashAllowed && (
              <button
                onClick={() => setShowCounterRequest(true)}
                className={prepayAllowed
                  ? 'mt-2 w-full h-12 rounded-xl border border-navy/30 font-display font-semibold text-sm text-navy tap-effect'
                  : 'w-full h-14 bg-navy text-white rounded-xl font-display font-semibold text-base flex items-center justify-center gap-2 tap-effect active:scale-[0.98] transition-transform'}
              >
                {!prepayAllowed && <ShoppingBag size={18} />}
                {t('counterRequest.startButton')}
              </button>
            )}
          </>
        )}
        {/* #1213 カウンター受け取り導線 — 対応店で、まだそのモードでない時に案内 */}
        {!takeoutMode && !deliveryMode && store.takeoutEnabled && !paymentBlocked && (
          <button
            onClick={() => setSearchParams({ mode: 'takeout' })}
            className="w-full rounded-xl border border-navy/20 py-2.5 font-body text-xs font-medium text-navy tap-effect"
          >
            {t('takeout.startCta')}
          </button>
        )}
        {/* #314 task #2: customers who arrived via a table QR are already
         * seated and self-ordering here. Making a reservation for later or
         * paying at the register (regular QR flow) would only confuse them
         * — the self-order checkout above handles everything. Only the
         * "predicted" intent is left visible. */}
        {/* #1248/#1292: 体験(チケット)を持つ店は「チケットを購入」。前払い→QR。
            1商品なら直接購入ページ、複数なら体験タブへ。予約(コース)とは別導線。

            **体験タブを今見ている時は出さない。** 複数商品の店では体験タブが
            既定タブなので、押しても「今いるタブに切り替える」だけで画面が
            変わらず、反応しないボタンになっていた。買う導線は各商品の行に
            並んでいるので、ここに重ねる意味も無い。 */}
        {hasExperience && !seatedTableNumber && !deliveryMode && !takeoutMode &&
         !(currentTab === 'experience' && experienceProducts.length > 1) && (
          <button
            onClick={() => {
              if (experienceProducts.length === 1) navigate(experienceBuyPath(experienceProducts[0].id, store?.isTrial === true))
              else setActiveTab('experience')
            }}
            className="w-full h-12 bg-navy text-white rounded-xl font-display font-semibold text-sm flex items-center justify-center gap-2 tap-effect active:scale-[0.98] transition-transform"
          >
            <Ticket size={18} />
            {t('storeDetail.buyTicket')}
          </button>
        )}
        {/* コース予約は飲食業種のみ (courseEnabled)。体験/おみやげ店では出さない。
          * #1683: 休業中 (paused) / 休会中は出さない。上の「お支払い・ご予約を
          * 受け付けていません」の下にボタンが並んでいると、押せると思って進んだ末に
          * サーバで弾かれる — 告知と挙動が食い違う。
          *
          * paymentBlocked ではなく isPaused で見る。KYC 未完了 (noPaymentAvailable)
          * の店でも**当日払いの予約はカード不要で成立する**ので、そこまで隠すと
          * 予約だけで回している店の入口を塞いでしまう。 */}
        {courseEnabled && !seatedTableNumber && !deliveryMode && !takeoutMode && !isPaused && (
          <button
            // #1246: 空き枠の閲覧はゲスト開放 (GET /reservations/slots は public)。
            // ログイン要求は確定の瞬間 (ReservationModal 内) まで後ろ倒し。
            onClick={() => setShowReservationModal(true)}
            className="w-full h-12 bg-navy text-white rounded-xl font-display font-semibold text-sm flex items-center justify-center gap-2 tap-effect active:scale-[0.98] transition-transform"
          >
            <CalendarDays size={18} />
            {t('storeDetail.reserveNow')}
          </button>
        )}
        {/* #972 QR/カード決済 (handlePayClick→Stripe) は現金のみ店では出さない。
          * #1683: 休業中 (paused) / 休会中も出さない (予約ボタンと同じ理由)。
          * #1769 現金のみ店でも KYC 済みでポイントを受ける店には「YOKOSO 通貨で支払う」を出す
          * (QRScan 側はカードを出さずポイントだけ)。 */}
        {/* #2115 メニュー閲覧のみの店の「ご注文はこちら」。飛び先は店が設定した
          * 他社のモバイルオーダー等。**別タブで開く** (戻ればメニューに戻れる)。
          * 卓ごとの URL は卓 QR から来た人にだけ出す (externalOrderUrl の解決側)。 */}
        {menuOnly && !!externalOrderUrl && (
          <a
            href={externalOrderUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full h-14 bg-yokoso-red text-white rounded-xl font-display font-semibold text-base flex items-center justify-center gap-2 tap-effect active:scale-[0.98] transition-transform"
          >
            <ExternalLink size={20} />
            {t('storeDetail.externalOrder')}
          </a>
        )}
        {menuOnly && !externalOrderUrl && (
          <p className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-2.5 font-body text-xs text-text-secondary leading-relaxed text-center">
            {t('storeDetail.menuOnlyNote')}
          </p>
        )}
        {!menuOnly && !seatedTableNumber && !deliveryMode && !takeoutMode && (cardInAppAllowed || pointsAllowed) && !paymentBlocked && (
          <button
            onClick={handlePayClick}
            className="w-full h-14 bg-yokoso-red text-white rounded-xl font-display font-semibold text-base flex items-center justify-center gap-2 tap-effect active:scale-[0.98] transition-transform"
          >
            {cardInAppAllowed ? <CreditCard size={20} /> : <Wallet size={20} />}
            {cardInAppAllowed ? t('storeDetail.payHere') : t('payment.methodYokoso')}
          </button>
        )}
      </div>

      {/* #2115 言語の選択。国旗だけでは何語か読めないので名前も出す */}
      {showLanguageSheet && (
        <div
          className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50 animate-fade-in"
          onClick={() => setShowLanguageSheet(false)}
        >
          <div
            className="w-full max-w-md rounded-t-2xl bg-white p-5 pb-8 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-base font-bold text-text-primary">
                {t('storeDetail.languageSwitch')}
              </h3>
              <button
                onClick={() => setShowLanguageSheet(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
                aria-label={t('common.close')}
              >
                <X size={16} className="text-text-secondary" />
              </button>
            </div>
            {/* 2 列の大きめの行にする。チップだと 11 個が詰まって押しにくい */}
            <div className="grid grid-cols-2 gap-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { setLocale(lang.code); setShowLanguageSheet(false) }}
                  className={`h-12 px-3 rounded-xl flex items-center gap-2 font-body text-sm tap-effect ${
                    locale === lang.code
                      ? 'bg-yokoso-red text-white font-semibold'
                      : 'bg-gray-50 text-text-primary'
                  }`}
                >
                  <span className="text-base">{lang.flag}</span>
                  <span className="truncate">{lang.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <ReservationModal
        open={showReservationModal}
        onClose={() => setShowReservationModal(false)}
        onRequireLogin={() => {
          stashReturnPath()
          navigate('/login')
        }}
        store={store}
      />

      {selfOrderActive && !deliveryMode && !takeoutMode && (
        <>
          <CartSheet
            allYouCanItemIds={coveredItemIdList}
            // 売り切れで弾かれた時にメニューを取り直す (売り切れ表示を最新化)
            onMenuStale={loadStore}
            open={showCartSheet}
            seatLabel={binderStore && seatedTableNumber
              ? t('binderBill.binderNo', { n: seatedTableNumber }) : undefined}
            onClose={() => setShowCartSheet(false)}
            requireTable={store.orderingMode === 'self-order'}
            onRequireLogin={() => {
              stashReturnPath()
              navigate('/login')
            }}
            onOrderPlaced={() => {
              setShowCartSheet(false)
              showToast({ type: 'success', message: t('cart.orderPlaced') })
              // Send the customer to their bill so they can see the new order
              // and access the お会計 button. The store-detail "お会計" CTA
              // also stays visible when they navigate back.
              navigate('/my-orders')
            }}
          />
          <CheckoutSheet
            open={showCheckoutSheet}
            storeId={store.id}
            storeName={store.name}
            unpaidOrders={unpaidForThisStore}
            onClose={() => setShowCheckoutSheet(false)}
            cardInAppAllowed={cardInAppAllowed}
          />
        </>
      )}

      {/* #1213 店頭受け取り (takeout)。CartSheet で中身を見て、下部 CTA から
       * TakeoutCheckoutSheet で先払いする (卓も住所も不要)。 */}
      {takeoutMode && cartForThisStore && (
        <>
          <CartSheet
            allYouCanItemIds={coveredItemIdList}
            // 売り切れで弾かれた時にメニューを取り直す (売り切れ表示を最新化)
            onMenuStale={loadStore}
            open={showCartSheet}
            onClose={() => setShowCartSheet(false)}
            hideOrderButton
          />
          <TakeoutCheckoutSheet
            open={showTakeoutCheckout}
            store={store}
            items={cartForThisStore.items}
            onClose={() => setShowTakeoutCheckout(false)}
            onMenuStale={loadStore}
          />
        </>
      )}

      {/* #1402 レジで払う (受付番号をレジで読んでもらう)。ログイン不要。
       *
       * #1541 **カートの生存に紐づけない**。発行成功時にシート自身が clearCart()
       * を呼ぶ (レジに出る内容を発行時点で確定させるため) ので、`cartForThisStore`
       * を条件に含めると `cart: null` と同時にこのシートごと unmount され、
       * **注文は立っているのに客に受付番号が出ない**状態になっていた。
       * 番号の表示は `issued` だけで完結しており items に依存しない。 */}
      {takeoutMode && store && (
        <CounterRequestSheet
          open={showCounterRequest}
          store={store}
          items={cartForThisStore?.items ?? []}
          onClose={() => setShowCounterRequest(false)}
          onMenuStale={loadStore}
        />
      )}

      {/* #284 delivery cart + checkout. CartSheet is reused (view items +
       * adjust quantity) but its "注文する" action is neutralised in
       * delivery mode — the bottom CTA opens DeliveryCheckoutSheet instead
       * which is the one path that actually creates the delivery order. */}
      {deliveryMode && cartForThisStore && (
        <>
          <CartSheet
            allYouCanItemIds={coveredItemIdList}
            // 売り切れで弾かれた時にメニューを取り直す (売り切れ表示を最新化)
            onMenuStale={loadStore}
            open={showCartSheet}
            onClose={() => setShowCartSheet(false)}
            hideOrderButton
          />
          <DeliveryCheckoutSheet
            open={showDeliveryCheckout}
            store={store}
            items={cartForThisStore.items}
            onClose={() => setShowDeliveryCheckout(false)}
            onMenuStale={loadStore}
          />
        </>
      )}

      <ShareSheet
        open={showShareSheet}
        onClose={() => setShowShareSheet(false)}
        storeId={store.id}
        storeName={store.name}
        snsMedia={store.snsMedia}
        source="store_detail"
      />
    </div>
  )
}
